// src/utils/results/recordingIdTime.js

/**
 * recordingId formats:
 *   2026-02-12T12-58-45.282Z-pzauv
 *   2026-02-12T12-58-45Z-pzauv
 *   2026-02-12T12:58:45.282Z-pzauv
 *
 * We parse the ISO-ish prefix up to the 'Z' and ignore the random suffix.
 */
export function parseTimestampFromRecordingId(recordingId) {
  if (!recordingId || typeof recordingId !== "string") return null;

  // capture: YYYY-MM-DD  HH  MM  SS  optional .mmm  (Z)
  const m = recordingId.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2})[-:](\d{2})[-:](\d{2})(?:\.(\d{1,3}))?Z/,
  );
  if (!m) return null;

  const [, ymd, hh, mm, ss, ms = "000"] = m;
  const iso = `${ymd}T${hh}:${mm}:${ss}.${ms.padEnd(3, "0")}Z`;

  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function getResultsTimeZone(locale = "en-GB") {
  const normalized = String(locale || "").toLowerCase();
  if (normalized.startsWith("ja")) return "Asia/Tokyo";
  if (normalized.startsWith("en-us")) return "America/New_York";
  return "Europe/London";
}

function getDateTimeParts(d, locale, timeZone) {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);

  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

export function formatDateParts(d, locale = "en-GB") {
  if (!(d instanceof Date) || !Number.isFinite(d.getTime())) {
    return { time: "", date: "" };
  }

  const timeZone = getResultsTimeZone(locale);
  const parts = getDateTimeParts(d, locale, timeZone);

  const hour = parts.hour === "24" ? "00" : parts.hour;
  const time = `${hour}:${parts.minute}`;
  const date = `${parts.day}.${parts.month}.${String(parts.year).slice(-2)}`;

  return { time, date };
}

export function formatRecordingIdDateParts(recordingId, locale = "en-GB") {
  return formatDateParts(parseTimestampFromRecordingId(recordingId), locale);
}

export function getLocalDayKeyMs(ms, locale = "en-GB") {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return null;

  const timeZone = getResultsTimeZone(locale);
  const parts = getDateTimeParts(d, locale, timeZone);
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);

  if (![year, month, day].every(Number.isFinite)) return null;
  return Date.UTC(year, month - 1, day);
}

export function formatDayMonth(ms, locale = "en-GB") {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";

  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
  });

  const parts = Object.fromEntries(
    formatter.formatToParts(d).map((part) => [part.type, part.value]),
  );

  return `${parts.day}/${parts.month}`;
}
